import { useCallback, useState, useEffect } from "react";
import { Button } from "@radix-ui/themes";
import { Dialog } from "../../components/shared/dialog";
import styles from "./Members.module.scss";
import Select from "../../components/shared/select";
import { useUpdateMember } from "../../api/member/Member.api";
import {
  Permission,
  PermissionDisplay,
  Role,
  Member,
  getMemberRole,
} from "../../api/member/Member.model";
import { toast } from "sonner";

import { useGetTenants } from "../../api/tenants";
import { Checkbox } from "@geist-ui/core";
import MultiSelect from "../../components/shared/multi-select/Select";

export function EditMemberDialog({
  member,
  refetch,
  open,
  onOpenChange,
}: {
  member: Member;
  refetch: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: tenants } = useGetTenants();

  const { mutateAsync: updateMember, isLoading } = useUpdateMember(member.id);

  const initialRole = getMemberRole(member);
  const [roleValue, setRoleValue] = useState<Role>(initialRole);
  const [allowedPermissions, setAllowedPermissions] = useState<Permission[]>(
    member.permissions.filter((p) => p !== Permission.COPILOT)
  );
  const [allowedTenants, setAllowedTenants] = useState<string[]>(
    member.tenants.map((t) => t.id)
  );

  useEffect(() => {
    if (open) {
      const currentRole = getMemberRole(member);
      setRoleValue(currentRole);
      setAllowedPermissions(
        member.permissions.filter((p) => p !== Permission.COPILOT)
      );
      setAllowedTenants(member.tenants.map((t) => t.id));
    }
  }, [open, member]);

  const rolesOptions = [
    { label: "Agent - Copilot AI assistant access", value: Role.AGENT },
    { label: "Member - Customized platform access", value: Role.MEMBER },
    { label: "Admin - Full administrative access", value: Role.ADMIN },
  ];

  const permissionsOptions = Object.entries(PermissionDisplay)
    .map((p) => {
      return {
        value: p[0] as Permission,
        label: p[1],
      };
    })
    .filter((p) => p.value !== Permission.COPILOT);

  const closeDialog = () => {
    onOpenChange(false);
  };

  const handleUpdate = useCallback(async () => {
    const isAdmin = roleValue === Role.ADMIN;
    const isAgent = roleValue === Role.AGENT;

    await updateMember(
      {
        permissions: isAgent
          ? [Permission.COPILOT]
          : [...allowedPermissions, Permission.COPILOT],
        tenantsIds: isAgent
          ? tenants?.map((t: any) => t.id) || []
          : allowedTenants,
        isAdmin,
      },
      {
        onSettled: () => {
          refetch();
          closeDialog();
          toast("Member updated");
        },
      }
    );
  }, [
    roleValue,
    allowedPermissions,
    allowedTenants,
    updateMember,
    refetch,
    tenants,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="large" id="dialog-members">
        <div className={styles.AddMember}>
          <Dialog.Close className={styles.Close} />

          <div className={styles.Header}>
            <div className={styles.Title}>Edit member</div>
          </div>
          <div className={styles.Content}>
            <div className={styles.AccessParameter}>
              <div className={styles.Option}>
                <div className={styles.Title}>Email</div>
              </div>

              <input
                type="email"
                value={member.email}
                disabled
                style={{
                  backgroundColor: "var(--gray-4)",
                  color: "var(--gray-9)",
                  cursor: "not-allowed",
                }}
              />
            </div>

            <div className={styles.AccessParameter}>
              <div className={styles.Option}>
                <div className={styles.Title}>Role</div>
              </div>

              <Select
                options={rolesOptions}
                value={roleValue}
                onChange={(newValue) => {
                  setRoleValue(newValue as Role);
                }}
                classes={{ trigger: styles.Select }}
              />
            </div>

            {roleValue === Role.MEMBER ? (
              <div className={styles.AccessParameter}>
                <div className={styles.Option}>
                  <div className={styles.Title}>Platform access</div>
                </div>

                <div className={styles.ChoiceList}>
                  {permissionsOptions.map((permission) => (
                    <Checkbox
                      className={styles.ChoiceItem}
                      key={permission.value}
                      scale={2}
                      checked={allowedPermissions.includes(permission.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAllowedPermissions((prev) => [
                            ...prev,
                            permission.value,
                          ]);
                        } else {
                          setAllowedPermissions((prev) =>
                            prev.filter((p) => p !== permission.value)
                          );
                        }
                      }}
                    >
                      <span>{permission.label}</span>
                    </Checkbox>
                  ))}
                </div>
              </div>
            ) : (
              <></>
            )}

            {roleValue === Role.MEMBER ? (
              <div className={styles.AccessParameter}>
                <div className={styles.Option}>
                  <div className={styles.Title}>Spaces access</div>
                </div>

                <MultiSelect
                  options={
                    tenants?.map((option) => ({
                      label: option.id,
                      value: option.id,
                    })) || []
                  }
                  value={allowedTenants}
                  onChange={setAllowedTenants}
                  classes={{ trigger: styles.MultiSelect }}
                />
              </div>
            ) : (
              <></>
            )}
          </div>

          <div className={styles.Footer}>
            <Button
              color="gray"
              variant="solid"
              highContrast
              onClick={handleUpdate}
              loading={false}
              disabled={isLoading}
              className={styles.AddButton}
            >
              {isLoading ? "..." : "Update Member"}
            </Button>
            <Button
              color="gray"
              variant="ghost"
              highContrast
              onClick={() => {
                closeDialog();
              }}
              className={styles.CancelButton}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
